import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings, useUpdateSettings } from '../hooks';
import { useSettingsFormStore } from '../store';
import { LanguageSelector } from './LanguageSelector';
import { ThemeSelector } from './ThemeSelector';
import { GenerationDefaultsForm } from './GenerationDefaultsForm';

export function SettingsForm() {
  const { t } = useTranslation();
  const { data: serverSettings, isLoading, isError } = useSettings();
  const updateMutation = useUpdateSettings();
  const store = useSettingsFormStore();
  const [statusMessage, setStatusMessage] = useState<{
    readonly type: 'success' | 'error';
    readonly text: string;
  } | null>(null);

  useEffect(() => {
    if (serverSettings) {
      const gd = serverSettings.generation_defaults;
      store.loadFromServer({
        language: serverSettings.language,
        theme: serverSettings.theme,
        default_checkpoint: gd?.default_checkpoint ?? null,
        default_sampler: gd?.default_sampler ?? 'euler',
        default_scheduler: gd?.default_scheduler ?? 'normal',
        default_steps: gd?.default_steps ?? 20,
        default_cfg_scale: gd?.default_cfg_scale ?? 7.0,
        default_width: gd?.default_width ?? 512,
        default_height: gd?.default_height ?? 512,
        default_negative_prompt: gd?.default_negative_prompt ?? '',
        default_pag: gd?.default_pag,
        default_freeu: gd?.default_freeu,
      });
    }
  }, [serverSettings]);

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [statusMessage]);

  const handleSave = () => {
    const request = {
      language: store.language,
      theme: store.theme,
      generation_defaults: {
        default_checkpoint: store.defaultCheckpoint,
        default_sampler: store.defaultSampler,
        default_scheduler: store.defaultScheduler,
        default_steps: store.defaultSteps,
        default_cfg_scale: store.defaultCfgScale,
        default_width: store.defaultWidth,
        default_height: store.defaultHeight,
        default_negative_prompt: store.defaultNegativePrompt,
        default_pag: {
          enabled: store.defaultPagEnabled,
          scale: store.defaultPagScale,
        },
        default_freeu: {
          enabled: store.defaultFreeUEnabled,
          b1: store.defaultFreeUB1,
          b2: store.defaultFreeUB2,
          s1: store.defaultFreeUS1,
          s2: store.defaultFreeUS2,
        },
      },
    };

    updateMutation.mutate(request, {
      onSuccess: () => {
        store.markClean();
        setStatusMessage({ type: 'success', text: t('settings.saved') });
      },
      onError: () => {
        setStatusMessage({ type: 'error', text: t('settings.saveError') });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-t-[var(--tg-theme-button-color)]" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-tg-hint animate-fade-in">
        <p>{t('settings.loadError')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div
        className="rounded-xl bg-tg-secondary space-y-4 p-4"
      >
        <LanguageSelector />
        <ThemeSelector />
      </div>

      <GenerationDefaultsForm />

      {statusMessage ? (
        <div
          className={`animate-fade-in rounded-lg px-4 py-2.5 text-sm text-center ${
            statusMessage.type === 'success' ? 'bg-tg-success/10 text-tg-success' : 'bg-tg-destructive/10 text-tg-destructive'
          }`}
        >
          {statusMessage.text}
        </div>
      ) : null}

      <button
        type="button"
        onClick={handleSave}
        disabled={!store.isDirty || updateMutation.isPending}
        className="w-full rounded-xl bg-gradient-to-br from-tg-button to-tg-button/80 px-4 py-3 text-sm font-semibold text-tg-button-text shadow-lg shadow-tg-button/20 transition-all duration-200 disabled:opacity-50 active:scale-[0.98]"
      >
        {updateMutation.isPending ? t('settings.saving') : t('settings.save')}
      </button>
    </div>
  );
}
